import {
  Fragment,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type CSSProperties,
  type DragEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
  type Ref,
  type UIEvent as ReactUIEvent,
} from "react";
import styled, { css } from "styled-components";

import { AnchoredPopup, useAnchoredPopupAnchor } from "../AnchoredPopup";
import { Checkbox } from "../Checkbox";
import { IconButton, type IconButtonSize } from "../IconButton";
import { Spinner } from "../Spinner";
import { DataTableAppendFooter, useDataTableAppend } from "./appendLoading";
import { Scrollbar } from "../Scrollbar";
import { Text, defaultTextVariants } from "../Text";
import { TRUNCATE_ATTR } from "../TruncatedText/truncation";
import { controlFocusRing, controlTransition } from "../foundations/controlStyles";
import { ChevronIcon, ChevronsUpDownIcon, DragHandleIcon } from "../../icons/glyphs";
import {
  resolveDataTableAppearanceRecipe,
  resolveDataTableRowAppearanceRecipe,
} from "./appearance";
import {
  DATA_TABLE_CELL_PADDING_BLOCK,
  DATA_TABLE_CELL_PADDING_BLOCK_TIGHT,
  DATA_TABLE_DEFAULT_MIN_WIDTH,
  DATA_TABLE_DEFAULT_ROW_MIN_HEIGHT,
  DATA_TABLE_HEADER_CELL_PADDING_BLOCK,
  SELECT_CHECKBOX_SIZE,
  buildDataTableGridMinWidth,
  buildDataTableGridTemplateColumns,
} from "./constants";
import {
  canToggleRowSelection,
  resolveSelectAllState,
  type DataTableSource,
} from "./DataTableSource";
import { isNestedInteractiveTarget, useRowActivation } from "./rowActivation";
import type { TableChildRow, TableRow, TableRowBase } from "./TableRow";
import type {
  DataTableAlign,
  DataTableAppearance,
  DataTableCellPadding,
  DataTableColumn,
  DataTableColumnVisibility,
  DataTableProps,
  DataTableRowAppearance,
  DataTableSortable,
  DataTableSortDirection,
  DataTableSortState,
} from "./types";

type DragDirection = "above" | "below";

type RowSelectionModifiers = {
  range: boolean;
  additive: boolean;
  toggle: boolean;
};

type SelectionAnchorTarget<Row extends TableRow> = {
  generation?: number;
  source: DataTableSource<Row>;
  rowId: string;
};

// 표현 계층: 테이블 프레임·헤더/행/셀·정렬·재정렬 UI 스타일 (styled-components 본문)
const Shell = styled.div<{
  $appearance: DataTableAppearance;
  $rowAppearance: DataTableRowAppearance;
}>`
  ${({ $appearance, $rowAppearance, theme }) => {
    const appearanceRecipe = resolveDataTableAppearanceRecipe(theme, $appearance);
    const rowRecipe = resolveDataTableRowAppearanceRecipe(theme, $rowAppearance);

    return css`
      --data-table-frame-border-width: ${appearanceRecipe.borderWidth};
      --data-table-frame-border-color: ${appearanceRecipe.borderColor};
      --data-table-frame-radius: ${appearanceRecipe.radius};
      --data-table-frame-surface: ${appearanceRecipe.surface};
      --data-table-row-gap: ${rowRecipe.gap};
      --data-table-header-gap: ${rowRecipe.headerGap};
      --data-table-row-radius: ${rowRecipe.radius};
      --data-table-row-divider: ${rowRecipe.divider};
      --data-table-row-group-end-divider-width: ${rowRecipe.groupEndDividerWidth};
      --data-table-row-surface-rest: ${rowRecipe.surface.rest};
      --data-table-row-surface-hover: ${rowRecipe.surface.hover};
      --data-table-row-surface-active: ${rowRecipe.surface.active};
      --data-table-row-surface-disabled: ${rowRecipe.surface.disabled};
      --data-table-row-surface-selected: ${rowRecipe.surface.selected};
      --data-table-row-surface-selected-hover: ${rowRecipe.surface.selectedHover};
      --data-table-row-surface-selected-active: ${rowRecipe.surface.selectedActive};
    `;
  }}

  display: flex;
  min-width: 0;
  min-height: 0;
  flex-direction: column;
  overflow: hidden;
  border-width: var(--data-table-frame-border-width);
  border-style: solid;
  border-color: var(--data-table-frame-border-color);
  border-radius: var(--data-table-frame-radius);
  background: var(--data-table-frame-surface);

  .grid {
    display: flex;
    width: 100%;
    min-width: 0;
    min-height: 0;
    flex-direction: column;
  }

  &[data-scrollable="true"] .grid,
  &[data-scrollable="true"] .scroller {
    flex: 1 1 auto;
  }

  /* 헤더는 body의 vertical scrollport 밖에 있지만 같은 horizontal coordinate를 쓴다.
     실제 body clientWidth는 동기화 hook이 inline-size로 내려 scrollbar gutter까지 맞춘다. */
  .headerViewport {
    overflow: hidden;
    width: 100%;
    max-width: 100%;
    flex: 0 0 auto;
    align-self: flex-start;
  }

  .gridTrack {
    width: 100%;
    min-width: max(var(--data-table-min-width), var(--data-table-grid-min-width));
  }

  .bodyGrid {
    display: flow-root;
  }

  .scroller {
    width: 100%;
    min-width: 0;
    min-height: 0;
  }

  /* box-sizing 은 행 높이 하한(rowMinHeight)이 보더를 포함한 값이 되도록 고정한다. 기본값
     auto 면 행에 height 제약이 없어 렌더 결과가 달라지지 않는다. */
  .row {
    position: relative;
    display: grid;
    box-sizing: border-box;
    border-bottom: 1px solid var(--data-table-row-divider);
  }

  /* parent + 현재 노출된 child가 하나의 시각 group이다. recipe는 radius/gap 값만 공급하고,
     어느 DOM row가 group 경계를 맡는지는 렌더 구조가 data marker로 결정한다. 첫 body group의
     leading gap도 row recipe가 소유해 raised header와 분리하고 continuous에서는 0으로 유지한다.
     group-end divider width도 recipe 값이라 continuous는 독립 행 사이 divider를 유지하고,
     raised는 parent/child group 내부 divider만 남긴다. */
  .row[data-row-group-start="true"] {
    --data-table-row-leading-gap: var(--data-table-row-gap);
    border-start-start-radius: var(--data-table-row-radius);
    border-start-end-radius: var(--data-table-row-radius);
  }

  .bodyRow[data-row-group-first="true"] {
    --data-table-row-leading-gap: var(--data-table-header-gap);
    margin-block-start: var(--data-table-header-gap);
  }

  .row[data-row-group-end="true"] {
    margin-block-end: var(--data-table-row-gap);
    border-bottom-width: var(--data-table-row-group-end-divider-width);
    border-end-start-radius: var(--data-table-row-radius);
    border-end-end-radius: var(--data-table-row-radius);
  }

  .bodyGrid > .row:last-child {
    border-bottom: 0;
  }

  .headerRow {
    background: transparent;
  }

  /* 행 높이 하한 — rowMinHeight prop 이 루트에 세우는 값이라 fallback 을 두지 않는다.
     기본값 auto 는 그 표에서 가장 높은 셀이 행 높이를 정하는 종전 동작이고, 셀 내용이 다른
     표끼리 리듬을 맞춰야 할 때 소비처가 하한을 준다. 헤더와 하위 행은 본문 행과 리듬이
     달라 하한을 공유하지 않는다. */
  .bodyRow {
    min-height: var(--data-table-row-min-height);
  }

  .bodyRow,
  .rowChildRow {
    background: var(--data-table-row-surface-rest);
    ${controlTransition("background-color")}
  }

  .bodyRow:hover {
    background: var(--data-table-row-surface-hover);
  }

  .clickableRow {
    cursor: pointer;
  }

  .clickableRow:active,
  .bodyRow.dragOverRow {
    background: var(--data-table-row-surface-active);
  }

  .focusableRow:focus-visible {
    z-index: 1;
    ${controlFocusRing("-2px")}
  }

  /* modifier를 누른 pointer focus는 브라우저가 :focus-visible로 판정할 수 있다. 행 focus와
     roving 위치는 보존하되 pointer-origin 표식이 있는 동안만 ring을 숨긴다. 실제 키보드
     입력이 오면 표식을 걷어 같은 :focus-visible ring을 다시 노출한다. */
  .focusableRow[data-data-table-pointer-focus]:focus-visible {
    outline: none;
  }

  /* checkbox가 없는 행 selection에서는 canSelect=false를 대신 보여 줄 control이 없다.
     selection 불가 행은 recipe의 muted surface/foreground를 쓰고 pointer hover·pressed에도
     그 면을 고정한다. 행 전체가 disabled인 것은 아니므로 control disabled 토큰까지 내리지 않는다.
     별도 activation이 있으면 cursor/focus/click은 유지하지만 selection surface 피드백은 만들지
     않는다. DnD drop target은 selection과 다른 입력이라 active 면을 복원한다. */
  .selectionDisabledRow,
  .selectionDisabledRow:hover,
  .selectionDisabledRow.clickableRow:active {
    background: var(--data-table-row-surface-disabled);
  }

  .selectionDisabledRow .cell,
  .selectionDisabledRow .indexCell,
  .selectionDisabledRow .rowChildCell {
    color: ${({ theme }) => theme.color.text.muted};
  }

  .selectionDisabledRow.dragOverRow {
    background: var(--data-table-row-surface-active);
  }

  .selectedRow {
    background: var(--data-table-row-surface-selected);
  }

  .selectedRow:hover {
    background: var(--data-table-row-surface-selected-hover);
  }

  .selectedRow.clickableRow:active,
  .selectedRow.dragOverRow {
    background: var(--data-table-row-surface-selected-active);
  }

  /* current(master-detail)는 source selection과 독립된 controlled 상태다. marker는 명시적으로
     opt-in한 최상위 current 행만 따르고 selected surface나 focus ring을 파생하지 않는다. */
  .bodyRow::before {
    position: absolute;
    z-index: 1;
    inset-block: 0;
    inset-inline-start: 0;
    width: 3px;
    border-start-start-radius: inherit;
    border-end-start-radius: inherit;
    background: ${({ theme }) => theme.color.border.selected};
    content: "";
    opacity: 0;
    pointer-events: none;
    ${controlTransition("opacity")}
  }

  .currentMarkerRow::before {
    opacity: 1;
  }

  /* 하위 행 — renderRowChild가 본 행 바로 뒤에 그리는 depth-1 행. 내용 셀 하나가 데이터
     컬럼 전 트랙을 span하는 subgrid라, 소비처 내용이 grid-column으로 표 컬럼 트랙에 정렬될
     수 있다. 선택·번호 같은 표 chrome 트랙은 span 밖이라 소비처가 쓰는 grid-column N은
     showCheckbox/showIndex와 무관하게 N번째 데이터 컬럼이다(시작 라인은 .grid의 CSS
     변수로 내려온다 — 트랙 수가 prop마다 달라 styled 상수로 굳힐 수 없다). */
  .rowChildCell {
    display: grid;
    min-width: 0;
    grid-column: var(--data-table-child-column-start, 1) / -1;
    grid-template-columns: subgrid;
  }

  /* drop line은 group boundary의 gap box 안에 놓인다. 첫 body group의 above는 headerGap,
     나머지 above/below는 row gap을 소비한다. gap=0인 continuous는 2px 선이 경계를 1px씩
     가로지른다. */
  .dropIndicator {
    position: absolute;
    z-index: 1;
    right: 0;
    left: 0;
    display: flex;
    align-items: center;
    pointer-events: none;
  }

  .dropIndicator::before {
    width: 100%;
    height: 2px;
    background: ${({ theme }) => theme.color.border.selected};
    content: "";
  }

  .dropAbove {
    top: min(-1px, calc(0px - var(--data-table-row-leading-gap)));
    height: max(2px, var(--data-table-row-leading-gap));
  }

  .dropBelow {
    bottom: min(-1px, calc(0px - var(--data-table-row-gap)));
    height: max(2px, var(--data-table-row-gap));
  }

  .headerCell,
  .cell,
  .indexCell,
  .actionsCell {
    display: flex;
    min-width: 0;
    align-items: center;
    padding-block: ${DATA_TABLE_CELL_PADDING_BLOCK};
    padding-inline: 8px;
  }

  /* column.cellPadding="tight" — 섬네일처럼 자체 높이를 가진 콘텐츠가 든 셀. 기본 여백이
     자기 높이에 더해지면 행 하한을 넘겨 그 표만 높아진다. 여백을 0 이 아니라 최소값으로
     남기는 이유는, 하한이 없거나 콘텐츠가 하한보다 커서 콘텐츠가 행 높이를 지배할 때
     여백이 0 이면 콘텐츠가 행 구분선에 그대로 맞닿기 때문이다.
     .cell 과 특정도가 같아 소스 순서로 이긴다 — 이 규칙을 위로 옮기지 말 것. */
  .cellTight {
    padding-block: ${DATA_TABLE_CELL_PADDING_BLOCK_TIGHT};
  }

  .headerCell,
  .indexHeaderCell {
    padding-block: ${DATA_TABLE_HEADER_CELL_PADDING_BLOCK};
    color: ${({ theme }) => theme.color.text.default};
    font-size: ${({ theme }) => theme.typography.size.caption};
    font-weight: ${({ theme }) => theme.typography.weight.extrabold};
    text-transform: uppercase;
  }

  /* 정렬 가능한 헤더는 라벨+인디케이터를 감싼 button으로 만들어 키보드(Enter/Space) 활성화를 얻는다. */
  .sortButton {
    display: inline-flex;
    min-width: 0;
    align-items: center;
    gap: 4px;
    margin: 0;
    padding: 0;
    border: 0;
    background: none;
    color: inherit;
    font: inherit;
    text-transform: inherit;
    letter-spacing: inherit;
    cursor: pointer;
  }

  .sortButton:focus-visible {
    border-radius: 4px;
    outline: 2px solid ${({ theme }) => theme.color.border.focus};
    outline-offset: 2px;
  }

  .cell {
    overflow: hidden;
    color: ${({ theme }) => theme.color.text.default};
    font-size: ${({ theme }) => theme.typography.size.body};
  }

  .cellContent {
    min-width: 0;
    overflow: hidden;
    /* Text는 기본 foreground를 직접 선언한다. DataTable이 소유하는 wrapper에서는 부모 셀의
       semantic foreground를 상속해야 header muted·selection-disabled 색이 실제 글자에 닿는다. */
    color: inherit;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .indexCell {
    justify-content: center;
    padding-right: 4px;
    padding-left: 4px;
    color: ${({ theme }) => theme.color.text.muted};
    font-size: ${({ theme }) => theme.typography.size.caption};
    font-weight: ${({ theme }) => theme.typography.weight.bold};
    user-select: none;
  }

  /* 선택 체크박스 열(좌측 끝, index 앞). 헤더·셀 모두 가운데 정렬. */
  .selectCell,
  .selectHeaderCell {
    display: flex;
    min-width: 0;
    align-items: center;
    justify-content: center;
    padding: 4px 4px;
  }

  .selectHeaderCell {
    padding-block: ${DATA_TABLE_HEADER_CELL_PADDING_BLOCK};
  }

  .actionsCell {
    justify-content: flex-end;
  }

  /* 재정렬 핸들(우측 끝 햄버거) 트랙. */
  .reorderHeaderCell,
  .reorderCell {
    display: flex;
    min-width: 0;
    align-items: center;
    justify-content: center;
    padding: 6px 4px;
  }

  .reorderHandle {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    margin: 0;
    padding: 0;
    border: 0;
    border-radius: 6px;
    background: none;
    color: ${({ theme }) => theme.color.text.muted};
    font-size: ${({ theme }) => theme.typography.size.title};
    cursor: grab;
  }

  .reorderHandle:hover {
    background: ${({ theme }) => theme.color.bg.surfaceSunken};
    color: ${({ theme }) => theme.color.text.default};
  }

  .reorderHandle:active {
    cursor: grabbing;
  }

  .reorderHandle:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.border.focus};
    outline-offset: 2px;
  }

  .empty {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 128px;
    padding: 24px;
    color: ${({ theme }) => theme.color.text.muted};
    font-size: ${({ theme }) => theme.typography.size.body};
  }

  .alignLeft {
    justify-content: flex-start;
    text-align: left;
  }

  .alignCenter {
    justify-content: center;
    text-align: center;
  }

  .alignRight {
    justify-content: flex-end;
    text-align: right;
  }

  @media (prefers-reduced-motion: reduce) {
    .bodyRow,
    .rowChildRow,
    .bodyRow::before,
    .rowChildRow::before {
      transition: none;
    }
  }
`;

// 미정렬은 중립적인 위/아래 쌍, 활성 정렬은 단일 방향으로 표시한다. 형태와 semantic text
// 위계를 함께 바꿔 dark header에서도 상태를 색 하나에만 의존하지 않고 구분한다.
const sortGlyphCss = css`
  flex: 0 0 auto;
  font-size: 0.8em;
  ${controlTransition("color", "transform")}

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const SortAffordanceGlyph = styled(ChevronsUpDownIcon)`
  ${sortGlyphCss}
  color: ${({ theme }) => theme.color.text.muted};
`;

// ChevronIcon은 아래 방향이라 asc일 때 180deg 회전해 위를 가리킨다.
const ActiveSortGlyph = styled(ChevronIcon)<{ $direction: DataTableSortDirection }>`
  ${sortGlyphCss}
  color: ${({ theme }) => theme.color.text.strong};
  transform: ${({ $direction }) => ($direction === "asc" ? "rotate(180deg)" : "none")};
`;

// 표현 계층: DataTable — 소스 snapshot 구독 + 정렬/재정렬/컬럼 가시성을 뷰에서 처리
export function DataTable<Row extends TableRow>({
  source,
  columns,
  columnVisibility: controlledColumnVisibility,
  emptyMessage = "No data.",
  loadingMessage = "Loading.",
  appendMessages,
  ariaLabel,
  appearance = "framed",
  rowAppearance = "continuous",
  showIndex = true,
  minWidth = DATA_TABLE_DEFAULT_MIN_WIDTH,
  rowMinHeight = DATA_TABLE_DEFAULT_ROW_MIN_HEIGHT,
  scrollable = false,
  actionsColumnWidth,
  rowClassName,
  renderRowChild,
  defaultRowChildrenExpanded = false,
  initialSortIndex,
  initialSort,
  currentRowId,
  rowSelection,
  showCheckbox = false,
  showCurrentMarker = false,
}: DataTableProps<Row>) {
  const [uncontrolledColumnVisibility] = useState<DataTableColumnVisibility>(() =>
    createDataTableColumnVisibility(columns),
  );
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [dragDirection, setDragDirection] = useState<DragDirection | null>(null);
  const [focusedRowId, setFocusedRowId] = useState<string | null>(null);
  // Shift anchor는 source-scoped transient state다. source가 교체되면 같은 row id가
  // 우연히 존재해도 이전 표의 입력을 새 표의 range 시작점으로 재사용하지 않는다.
  const selectionAnchorTargetRef = useRef<SelectionAnchorTarget<Row> | null>(null);
  const rowElementRefs = useRef(new Map<string, HTMLElement>());
  // 재정렬 source(canReorderRows)면 정렬은 끈다(상호 배타, 재정렬 우선).
  const sortingEnabled = !source.canReorderRows;
  const [sort, setSortState] = useState<DataTableSortState | null>(() =>
    sortingEnabled ? resolveInitialSort(columns, initialSort, initialSortIndex) : null,
  );
  const sourceSnapshot = useSyncExternalStore(
    source.subscribe,
    source.getSnapshot,
    source.getSnapshot,
  );
  const rows = sourceSnapshot.rows;
  const columnVisibility = controlledColumnVisibility ?? uncontrolledColumnVisibility;
  const rowSelectionMode = rowSelection?.mode ?? "none";
  const multipleSelectionBehavior =
    rowSelection?.mode === "multiple" ? rowSelection.behavior : null;
  const rowSelectionEnabled = rowSelection !== undefined;
  const selectionSurfaceEnabled = showCheckbox || rowSelectionEnabled;
  const multipleSelectionEnabled = showCheckbox || rowSelectionMode === "multiple";
  const rootRole = rowSelectionEnabled ? "grid" : "table";
  const bodyCellRole = rowSelectionEnabled ? "gridcell" : "cell";
  const getSelectionAnchorRowId = useCallback(
    (): string | null =>
      selectionAnchorTargetRef.current?.source === source &&
      selectionAnchorTargetRef.current.generation === sourceSnapshot.append?.generation
        ? selectionAnchorTargetRef.current.rowId
        : null,
    [source, sourceSnapshot.append?.generation],
  );
  const setSelectionAnchorRowId = useCallback(
    (rowId: string) => {
      selectionAnchorTargetRef.current = {
        source,
        rowId,
        generation: sourceSnapshot.append?.generation,
      };
    },
    [source, sourceSnapshot.append?.generation],
  );

  // 파생 상태: 보이는 컬럼·행 액션 유무·그리드 트랙(컬럼 템플릿/최소 폭)
  const isColumnVisible = useCallback(
    (column: DataTableColumn<Row>) => columnVisibility[column.key] ?? true,
    [columnVisibility],
  );
  const visibleColumns = useMemo(() => columns.filter(isColumnVisible), [columns, isColumnVisible]);
  const hasRowActions = useMemo(() => rows.some((row) => row.hasRowActions), [rows]);
  const isReorderable = source.canReorderRows;
  const gridTemplateColumns = useMemo(
    () =>
      buildDataTableGridTemplateColumns(visibleColumns, {
        showSelection: showCheckbox,
        showIndex,
        showActions: hasRowActions,
        actionsColumnWidth,
        showReorderHandle: isReorderable,
      }),
    [actionsColumnWidth, hasRowActions, isReorderable, showCheckbox, showIndex, visibleColumns],
  );
  // column track 최소 폭 합을 grid min-width에 반영해 overflow 구간도 row 배경/구분선이 끝까지 그려지게 한다.
  const gridMinWidth = useMemo(
    () =>
      buildDataTableGridMinWidth(visibleColumns, {
        showSelection: showCheckbox,
        showIndex,
        showActions: hasRowActions,
        actionsColumnWidth,
        showReorderHandle: isReorderable,
      }),
    [actionsColumnWidth, hasRowActions, isReorderable, showCheckbox, showIndex, visibleColumns],
  );
  // 하위 행 내용 셀이 시작하는 그리드 라인 = 첫 데이터 컬럼. 선택·번호 트랙은 하위 행에서도
  // 별도 셀이라 span 밖이고, 그 덕에 소비처가 쓰는 트랙 번호가 chrome prop과 무관해진다.
  const childContentColumnStart = 1 + (showCheckbox ? 1 : 0) + (showIndex ? 1 : 0);
  const gridStyle = useMemo(
    () =>
      ({
        "--data-table-min-width": minWidth,
        "--data-table-grid-min-width": gridMinWidth,
        "--data-table-child-column-start": String(childContentColumnStart),
        "--data-table-row-min-height": rowMinHeight,
      }) as CSSProperties,
    [childContentColumnStart, gridMinWidth, minWidth, rowMinHeight],
  );
  const rowStyle = useMemo<CSSProperties>(
    () => ({
      gridTemplateColumns,
    }),
    [gridTemplateColumns],
  );
  const { headerViewportRef, bodyScrollerRef, bodyGridRef, handleBodyScroll, handleHeaderScroll } =
    useDataTableHeaderScrollSync(scrollable);
  const { sentinelRef, requestMore } = useDataTableAppend(
    source,
    sourceSnapshot,
    bodyScrollerRef,
    scrollable,
  );
  // 하위 행의 내용 셀 하나가 대신하는 컬럼 수 — chrome 트랙(선택·번호)은 별도 셀이라 뺀다.
  const childContentColumnSpan =
    visibleColumns.length + (hasRowActions ? 1 : 0) + (isReorderable ? 1 : 0);
  const isInitialLoading = sourceSnapshot.loadState === "loading" && rows.length === 0;
  const { onActivateClick, onActivateKeyDown } = useRowActivation<Row>();

  // 정렬: 클라이언트/서버 정렬 + 헤더 3-state 토글
  // 클라이언트 정렬: 로드된 행을 compare로 정렬한다(안정 정렬). 서버 정렬 source는 그대로 둔다.
  const displayRows = useMemo(() => {
    if (!sort || source.sortsOnServer) {
      return rows;
    }
    const factor = sort.direction === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => factor * a.compare(b, sort.columnKey));
  }, [rows, sort, source]);

  // source selection: showCheckbox는 checkbox 열, rowSelection은 행 입력만 켠다. 둘 중 하나가
  // 보이는 상태면 selected surface/ARIA도 source snapshot을 따른다. 헤더 전체선택의
  // 대상·checked·indeterminate는 공유 계산(resolveSelectAllState)이 소유한다.
  const selectedIds = sourceSnapshot.selectedIds;
  // 선택 단위 — 자식을 **선언한** 행은 그 자식들이, 선언하지 않은 행은 자신이 단위다.
  // 판정에 canShow()를 쓰지 않는 것이 핵심: 접힘(자식 canShow=false)에서 펼침으로 넘어갈 때
  // 단위가 "부모 자신"↔"자식들"로 뒤집히면, 접힌 채 담아둔 부모 선택이 UI에서 사라진 채
  // 저장소에만 남는 고아가 된다(PIT-visibility-selection-identity).
  const resolveDeclaredChildRows = useCallback(
    (row: Row): readonly TableChildRow[] => (renderRowChild ? row.getRowChildren() : []),
    [renderRowChild],
  );
  const resolveSelectionUnit = useCallback(
    (row: Row): readonly (Row | TableChildRow)[] => {
      const declaredChildRows = resolveDeclaredChildRows(row);
      return declaredChildRows.length > 0 ? declaredChildRows : [row];
    },
    [resolveDeclaredChildRows],
  );
  const canUseSelectionUnit = useCallback(
    (selectionUnit: readonly (Row | TableChildRow)[]): boolean =>
      multipleSelectionBehavior === "toggle"
        ? resolveSelectAllState(selectionUnit, selectedIds, source).selectableRows.length > 0
        : canReplaceRowSelectionUnit(selectionUnit, selectedIds),
    [multipleSelectionBehavior, selectedIds, source],
  );
  const canUseRowSelection = useCallback(
    (row: Row): boolean => canUseSelectionUnit(resolveSelectionUnit(row)),
    [canUseSelectionUnit, resolveSelectionUnit],
  );
  // checkbox disabled와 같은 자격을 roving focus에도 반영한다. 다만 selection과 독립된
  // activation이 있는 행은 selection 불가여도 그 activation의 focus target으로 남는다.
  const navigableRows = useMemo(
    () =>
      rowSelectionEnabled
        ? displayRows.filter((row) => row.isRowClickable || canUseRowSelection(row))
        : [],
    [canUseRowSelection, displayRows, rowSelectionEnabled],
  );
  const rovingFocusRowId = rowSelectionEnabled
    ? navigableRows.some((row) => row.getRowId() === focusedRowId)
      ? focusedRowId
      : (navigableRows[0]?.getRowId() ?? null)
    : null;

  // 행 클릭 selection은 top-level selection unit을 쓴다. Shift 범위는 source가 제공하는
  // 전체 필터·정렬 결과 또는 현재 표시 순서(displayRows)에서 두 끝점을 id로 찾는다.
  // single과 multiple/replace의 일반 입력은 replaceSelection으로 원자 교체한다.
  // multiple/toggle의 일반 입력과 multiple/replace의 modifier 입력은 기존 묶음을 토글/확장한다.
  // Shift anchor는 id로 보관해 정렬 뒤 index가 바뀌어도 현재 범위 집합에서 찾는다.
  const applyRowSelection = useCallback(
    (row: Row, rowIndex: number, modifiers: RowSelectionModifiers) => {
      if (!rowSelection) return;

      const rowUnit = resolveSelectionUnit(row);
      const canParticipate = canUseSelectionUnit(rowUnit);
      if (!canParticipate) return;

      if (rowSelection.mode === "single") {
        source.replaceSelection(rowUnit);
        setSelectionAnchorRowId(row.getRowId());
        return;
      }

      const selectionAnchorRowId = getSelectionAnchorRowId();
      const rangeRows =
        modifiers.range && selectionAnchorRowId !== null
          ? (source.getSelectionRangeRows()?.filter((candidate) => candidate.canShow()) ??
            displayRows)
          : displayRows;
      const anchorIndex =
        selectionAnchorRowId !== null
          ? rangeRows.findIndex((candidate) => candidate.getRowId() === selectionAnchorRowId)
          : -1;
      const targetIndex =
        rangeRows === displayRows
          ? rowIndex
          : rangeRows.findIndex((candidate) => candidate.getRowId() === row.getRowId());
      // source의 최신 범위 집합에서 사라진 대상을 이전 페이지 객체로 선택하지 않는다.
      if (modifiers.range && targetIndex < 0) return;
      if (modifiers.range && anchorIndex >= 0) {
        const start = Math.min(anchorIndex, targetIndex);
        const end = Math.max(anchorIndex, targetIndex);
        const rangeUnits = rangeRows.slice(start, end + 1).flatMap(resolveSelectionUnit);
        const rangeCanParticipate = canUseSelectionUnit(rangeUnits);
        if (!rangeCanParticipate) return;

        if (rowSelection.behavior === "toggle" || modifiers.additive) {
          source.setRowsSelected(rangeUnits, true);
        } else {
          source.replaceSelection(rangeUnits);
        }
        return;
      }

      if (rowSelection.behavior === "toggle" || modifiers.toggle) {
        // 부모 집계 체크박스와 같은 대상/checked 판정을 쓴다. 선택 불가한 미선택 child가 unit에
        // 섞여 있어도, 조작 가능한 child가 전부 선택됐다면 행 입력으로 다시 해제할 수 있어야 한다.
        const unitSelection = resolveSelectAllState(rowUnit, selectedIds, source);
        source.setRowsSelected(unitSelection.selectableRows, !unitSelection.allSelected);
      } else {
        source.replaceSelection(rowUnit);
      }
      setSelectionAnchorRowId(row.getRowId());
    },
    [
      displayRows,
      canUseSelectionUnit,
      getSelectionAnchorRowId,
      resolveSelectionUnit,
      rowSelection,
      selectedIds,
      setSelectionAnchorRowId,
      source,
    ],
  );

  const handleInteractiveRowClick = useCallback(
    (event: ReactMouseEvent<HTMLElement>, row: Row, rowIndex: number) => {
      if (rowSelectionEnabled && !isNestedInteractiveTarget(event.target, event.currentTarget)) {
        const additive = event.ctrlKey || event.metaKey;
        applyRowSelection(row, rowIndex, {
          range: event.shiftKey,
          additive,
          toggle: additive && !event.shiftKey,
        });
      }
      onActivateClick(event, row);
    },
    [applyRowSelection, onActivateClick, rowSelectionEnabled],
  );

  const handleInteractiveRowMouseDown = useCallback(
    (event: ReactMouseEvent<HTMLElement>) => {
      if (event.button !== 0 || isNestedInteractiveTarget(event.target, event.currentTarget)) {
        return;
      }

      event.currentTarget.setAttribute("data-data-table-pointer-focus", "");
      if (rowSelectionMode !== "multiple" || !event.shiftKey) return;

      // Shift+click은 range selection 입력이므로 브라우저의 텍스트 범위 확장을 함께
      // 실행하지 않는다. mousedown 기본 focus도 취소되므로 roving focus는 명시적으로 옮긴다.
      event.preventDefault();
      event.currentTarget.focus({ preventScroll: true });
    },
    [rowSelectionMode],
  );

  const handleInteractiveRowKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLElement>, row: Row, rowIndex: number) => {
      const nestedInteractive = isNestedInteractiveTarget(event.target, event.currentTarget);
      const modifierOnlyKey =
        event.key === "Shift" ||
        event.key === "Control" ||
        event.key === "Alt" ||
        event.key === "Meta";
      if (rowSelectionEnabled && !nestedInteractive && !modifierOnlyKey) {
        event.currentTarget.removeAttribute("data-data-table-pointer-focus");
      }
      if (rowSelectionEnabled && !nestedInteractive && event.key === " ") {
        event.preventDefault();
        const additive = event.ctrlKey || event.metaKey;
        applyRowSelection(row, rowIndex, {
          range: event.shiftKey,
          additive,
          // Space는 checkbox와 같은 토글 입력이다. Shift+Space만 anchor 범위로 해석한다.
          toggle: !event.shiftKey,
        });
      }

      if (
        !nestedInteractive &&
        (event.key === "ArrowDown" || event.key === "End") &&
        rowIndex >= displayRows.length - 1
      )
        requestMore();

      if (rowSelectionEnabled && !nestedInteractive) {
        const navigationIndex = navigableRows.findIndex(
          (candidate) => candidate.getRowId() === row.getRowId(),
        );
        let focusIndex: number | null = null;
        if (event.key === "ArrowDown") {
          focusIndex = Math.min(navigationIndex + 1, navigableRows.length - 1);
        }
        if (event.key === "ArrowUp") focusIndex = Math.max(navigationIndex - 1, 0);
        if (event.key === "Home") focusIndex = 0;
        if (event.key === "End") focusIndex = navigableRows.length - 1;

        if (focusIndex !== null && focusIndex >= 0) {
          event.preventDefault();
          if (
            focusIndex === navigableRows.length - 1 &&
            (event.key === "ArrowDown" || event.key === "End")
          )
            requestMore();
          const focusRow = navigableRows[focusIndex];
          if (focusRow && focusRow.getRowId() !== row.getRowId()) {
            const focusDisplayIndex = displayRows.findIndex(
              (candidate) => candidate.getRowId() === focusRow.getRowId(),
            );
            if (event.shiftKey && rowSelectionMode === "multiple") {
              if (getSelectionAnchorRowId() === null) {
                setSelectionAnchorRowId(row.getRowId());
              }
              applyRowSelection(focusRow, focusDisplayIndex, {
                range: true,
                additive: event.ctrlKey || event.metaKey,
                toggle: false,
              });
            }
            const focusRowId = focusRow.getRowId();
            setFocusedRowId(focusRowId);
            rowElementRefs.current.get(focusRowId)?.focus();
          }
        }
      }
      onActivateKeyDown(event, row);
    },
    [
      applyRowSelection,
      displayRows,
      getSelectionAnchorRowId,
      navigableRows,
      onActivateKeyDown,
      requestMore,
      rowSelectionMode,
      rowSelectionEnabled,
      setSelectionAnchorRowId,
    ],
  );
  // 헤더 전체선택 후보 — 현재 페이지 행들의 선택 단위를 편 것.
  const selectionCandidates = useMemo<readonly (Row | TableChildRow)[]>(
    () => (showCheckbox ? displayRows.flatMap(resolveSelectionUnit) : []),
    [displayRows, resolveSelectionUnit, showCheckbox],
  );
  const {
    selectableRows,
    allSelected: allCandidatesSelected,
    someSelected: someCandidatesSelected,
  } = useMemo(
    () =>
      showCheckbox
        ? resolveSelectAllState(selectionCandidates, selectedIds, source)
        : {
            selectableRows: [] as readonly (Row | TableChildRow)[],
            allSelected: false,
            someSelected: false,
          },
    [selectionCandidates, selectedIds, showCheckbox, source],
  );

  // 서버 정렬 source에는 활성 정렬을 통지한다(source가 재조회·캐시 무효화 담당).
  useEffect(() => {
    if (source.sortsOnServer) {
      source.setSort(sort);
    }
  }, [sort, source]);

  const handleSortToggle = useCallback((column: DataTableColumn<Row>) => {
    setSortState((current) => cycleSort(current, column));
  }, []);

  // 재정렬: 우측 핸들 드래그 + 드롭 인디케이터, 실제 drop에서만 source.reorderRows 확정
  const handleDragStart = useCallback(
    (event: DragEvent<HTMLElement>, index: number) => {
      if (!isReorderable) {
        return;
      }
      event.dataTransfer.effectAllowed = "move";
      setDragIndex(index);

      // 드래그 중 커서를 따라다닐 고스트: 마우스다운 시점 행을 낮은 opacity로 복제한 스냅샷.
      // 브라우저가 dragstart 직후 이미지를 캡처하므로 캡처 뒤 정리한다.
      const rowEl = event.currentTarget.closest<HTMLElement>(".bodyRow");
      if (rowEl && typeof event.dataTransfer.setDragImage === "function") {
        const ghost = buildRowDragImage(rowEl);
        if (ghost) {
          const rect = rowEl.getBoundingClientRect();
          event.dataTransfer.setDragImage(
            ghost,
            event.clientX - rect.left,
            event.clientY - rect.top,
          );
          window.setTimeout(() => ghost.remove(), 0);
        }
      }
    },
    [isReorderable],
  );

  const handleDragOver = useCallback(
    (event: DragEvent<HTMLDivElement>, index: number) => {
      if (!isReorderable || dragIndex === null || dragIndex === index) {
        setDragOverIndex(null);
        setDragDirection(null);
        return;
      }
      event.preventDefault();
      event.dataTransfer.dropEffect = "move";
      setDragOverIndex(index);
      setDragDirection(index < dragIndex ? "above" : "below");
    },
    [dragIndex, isReorderable],
  );

  const handleDrop = useCallback(
    (event: DragEvent<HTMLDivElement>, index: number) => {
      // 재정렬은 실제 drop에서만 확정한다. drop 대상 행 위치(index)에 끌던 행을 끼워 넣는다.
      event.preventDefault();
      if (isReorderable && dragIndex !== null && dragIndex !== index) {
        const reorderedRows = Array.from(rows);
        const [movedRow] = reorderedRows.splice(dragIndex, 1);
        if (movedRow) {
          reorderedRows.splice(index, 0, movedRow);
          source.reorderRows(reorderedRows);
        }
      }
      setDragIndex(null);
      setDragOverIndex(null);
      setDragDirection(null);
    },
    [dragIndex, isReorderable, rows, source],
  );

  const handleDragEnd = useCallback(() => {
    // drop이 아닌 종료(테이블 밖에 놓거나 취소)는 재정렬을 확정하지 않고 상태만 초기화한다.
    setDragIndex(null);
    setDragOverIndex(null);
    setDragDirection(null);
  }, []);

  // 렌더: 고정 헤더 + body scrollport + 로드 상태/본문 행(인덱스·셀·액션·재정렬 핸들)
  return (
    <Shell
      $appearance={appearance}
      $rowAppearance={rowAppearance}
      data-appearance={appearance}
      data-row-appearance={rowAppearance}
      data-scrollable={scrollable ? "true" : "false"}
    >
      <div
        className="grid"
        style={gridStyle}
        role={rootRole}
        aria-busy={
          sourceSnapshot.append ? isInitialLoading || sourceSnapshot.append.loadingMore : undefined
        }
        aria-label={ariaLabel}
        aria-multiselectable={rowSelectionEnabled && multipleSelectionEnabled ? true : undefined}
      >
        <div className="headerViewport" ref={headerViewportRef} onScroll={handleHeaderScroll}>
          <div className="row headerRow gridTrack" style={rowStyle} role="row">
            {showCheckbox ? (
              // 접근성 이름은 Checkbox가 소유한다(body cell과 동일 패턴). columnheader는 별도
              // aria-label을 두지 않고 내부 Checkbox 이름을 content로 상속한다 — 라벨 중복 제거.
              <div className="selectHeaderCell" role="columnheader">
                <Checkbox
                  checked={allCandidatesSelected}
                  indeterminate={someCandidatesSelected && !allCandidatesSelected}
                  disabled={selectableRows.length === 0}
                  onChange={(event) => source.setRowsSelected(selectableRows, event.target.checked)}
                  aria-label={
                    sourceSnapshot.append
                      ? "Select all loaded rows"
                      : "Select all rows on this page"
                  }
                  size={SELECT_CHECKBOX_SIZE}
                />
              </div>
            ) : null}
            {showIndex ? (
              <div
                className="indexCell indexHeaderCell"
                role="columnheader"
                aria-label="Row number"
              >
                <Text variant={defaultTextVariants["caption-bold"]} className="cellContent">
                  #
                </Text>
              </div>
            ) : null}
            {visibleColumns.map((column) => {
              const columnSortable = sortingEnabled && (column.sortable ?? "none") !== "none";
              const activeDirection = sort?.columnKey === column.key ? sort.direction : null;
              return (
                <div
                  key={column.key}
                  className={`headerCell ${resolveAlignClassName(column.align)}`}
                  role="columnheader"
                  aria-sort={columnSortable ? ariaSortValue(activeDirection) : undefined}
                >
                  {columnSortable ? (
                    <button
                      type="button"
                      className="sortButton"
                      onClick={() => handleSortToggle(column)}
                    >
                      <Text variant={defaultTextVariants["caption-bold"]} className="cellContent">
                        {column.header}
                      </Text>
                      {activeDirection === null ? (
                        <SortAffordanceGlyph />
                      ) : (
                        <ActiveSortGlyph $direction={activeDirection} />
                      )}
                    </button>
                  ) : (
                    <Text variant={defaultTextVariants["caption-bold"]} className="cellContent">
                      {column.header}
                    </Text>
                  )}
                </div>
              );
            })}
            {hasRowActions ? (
              <div className="headerCell" role="columnheader" aria-label="Actions" />
            ) : null}
            {isReorderable ? (
              <div className="reorderHeaderCell" role="columnheader" aria-label="Reorder" />
            ) : null}
          </div>
        </div>

        <DataTableScroller
          scrollable={scrollable}
          scrollerRef={bodyScrollerRef}
          onScroll={handleBodyScroll}
        >
          <div className="bodyGrid gridTrack" ref={bodyGridRef}>
            {isInitialLoading ? (
              <Text variant={defaultTextVariants["body-normal"]} className="empty">
                {sourceSnapshot.append ? <Spinner size="sm" /> : null}
                {loadingMessage}
              </Text>
            ) : sourceSnapshot.loadState === "failed" && rows.length === 0 ? (
              sourceSnapshot.append ? null : (
                <Text variant={defaultTextVariants["body-normal"]} className="empty">
                  {sourceSnapshot.errorMessage ?? emptyMessage}
                </Text>
              )
            ) : rows.length === 0 ? (
              <Text variant={defaultTextVariants["body-normal"]} className="empty">
                {emptyMessage}
              </Text>
            ) : (
              displayRows.map((row, rowIndex) => {
                // 하위 행은 본 행 바로 뒤 별도 행으로 붙는다 — displayRows 밖에서 렌더되므로
                // 행 번호(rowIndex)를 밀지 않는다. 그리는 대상은 펼쳐졌고 보이는 자식뿐이지만,
                // 선택 단위는 접힘/펼침에 흔들리지 않게 **선언된** 자식 전체다.
                const declaredChildRows = resolveDeclaredChildRows(row);
                // 펼침(부모 상태)과 가시성(자식 canShow — 권한 등)은 별개 축이라 따로 통과시킨다.
                // 펼침은 행이 정한 값이 이기고, 아직 정하지 않은 행(undefined)만 표 기본값이
                // 채운다 — 기본값을 OR로 덮어씌우면 소비처 캐럿이 접어도 다시 펴져 접기가 죽는다.
                const expanded = row.isRowChildrenExpanded ?? defaultRowChildrenExpanded;
                const childRows = expanded
                  ? declaredChildRows.filter((child) => child.canShow())
                  : [];
                const hasVisibleChildRows = childRows.length > 0;
                const isDropTarget = dragOverIndex === rowIndex && dragDirection !== null;
                // 자식을 선언한 부모의 체크박스는 자식 집계 토글이다(헤더 전체선택의 축소판).
                // 접혀 있어도 집계라, 여기서 담은 선택이 펼친 뒤 그대로 자식 체크로 보인다.
                const childSelection =
                  selectionSurfaceEnabled && declaredChildRows.length > 0
                    ? resolveSelectAllState(declaredChildRows, selectedIds, source)
                    : null;
                const rowId = row.getRowId();
                // selection은 checkbox가 checked인 상태를, current는 master-detail의 현재 항목을
                // 각각 표현한다. 같은 id일 수 있지만 서로를 파생하거나 덮어쓰지 않는다.
                const isRowSelected =
                  selectionSurfaceEnabled &&
                  (childSelection ? childSelection.allSelected : selectedIds.has(rowId));
                const isCurrentRow = currentRowId === rowId;
                // source selection만 selected surface를 소유한다. current는 aria-current와 명시적
                // marker만 소유하므로 두 상태는 같은 행에 겹치거나 서로 다른 행을 가리킬 수 있다.
                const hasCurrentMarker = showCurrentMarker && isCurrentRow;
                const rowSelectionDisabled = rowSelectionEnabled && !canUseRowSelection(row);
                // selection과 activation은 독립이다. selection이 막혀도 domain activation이 있으면
                // pointer/focus 입력은 남고, 둘 다 없을 때만 행 surface가 비활성 target이 된다.
                const rowClickable =
                  row.isRowClickable || (rowSelectionEnabled && !rowSelectionDisabled);
                const rowFocusable =
                  row.isRowClickable || (rowSelectionEnabled && !rowSelectionDisabled);
                const exposeRowSelectionAria =
                  selectionSurfaceEnabled && (!rowSelectionEnabled || !rowSelectionDisabled);
                return (
                  <Fragment key={rowId}>
                    <div
                      ref={(element) => {
                        if (element) {
                          rowElementRefs.current.set(rowId, element);
                        } else {
                          rowElementRefs.current.delete(rowId);
                        }
                      }}
                      className={resolveRowClassName({
                        clickable: rowClickable,
                        focusable: rowFocusable,
                        dragOver: dragOverIndex === rowIndex,
                        // source selection input이 있으면 행 강조 = 그 selection unit이 selected.
                        // 집계 부모는 자기 id 대신 자식이 전부 담겼을 때 강조된다(부분 선택은 미강조).
                        selected: isRowSelected,
                        currentMarker: hasCurrentMarker,
                        selectionDisabled: rowSelectionDisabled,
                        extra: rowClassName?.(row),
                      })}
                      style={rowStyle}
                      data-row-group-start="true"
                      data-row-group-end={hasVisibleChildRows ? "false" : "true"}
                      data-row-group-first={rowIndex === 0 ? "true" : undefined}
                      // 정적/checkbox-only 표와 interactive selection grid 모두 행은 role="row"다.
                      // current와 source selection은 같은 행에서 독립된 ARIA 상태로 남는다.
                      role="row"
                      aria-current={isCurrentRow ? "true" : undefined}
                      aria-selected={exposeRowSelectionAria ? isRowSelected : undefined}
                      tabIndex={
                        !rowFocusable
                          ? undefined
                          : rowSelectionEnabled
                            ? rovingFocusRowId === rowId
                              ? 0
                              : -1
                            : 0
                      }
                      onFocus={
                        rowSelectionEnabled && rowFocusable
                          ? () => setFocusedRowId(rowId)
                          : undefined
                      }
                      onMouseDown={
                        rowSelectionEnabled && rowFocusable
                          ? handleInteractiveRowMouseDown
                          : undefined
                      }
                      onBlur={
                        rowFocusable
                          ? (event) =>
                              event.currentTarget.removeAttribute("data-data-table-pointer-focus")
                          : undefined
                      }
                      onClick={
                        rowClickable
                          ? (event) => handleInteractiveRowClick(event, row, rowIndex)
                          : undefined
                      }
                      onKeyDown={
                        rowFocusable
                          ? (event) => handleInteractiveRowKeyDown(event, row, rowIndex)
                          : undefined
                      }
                      onDragOver={(event) => handleDragOver(event, rowIndex)}
                      onDrop={(event) => handleDrop(event, rowIndex)}
                    >
                      {isDropTarget &&
                      (dragDirection === "above" ||
                        (dragDirection === "below" && !hasVisibleChildRows)) ? (
                        <div
                          className={`dropIndicator ${dragDirection === "above" ? "dropAbove" : "dropBelow"}`}
                        />
                      ) : null}
                      {showCheckbox ? (
                        // 체크박스 클릭이 행 활성화(상세 열기)로 흡수되지 않게 전파만 끊는다. 토글은
                        // Checkbox가 담당하므로 셀 자체는 키보드 인터랙션 대상이 아니다.
                        // eslint-disable-next-line jsx-a11y/click-events-have-key-events
                        <div
                          className="selectCell"
                          role={bodyCellRole}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {childSelection ? (
                            // 자식 집계 토글 — 부모 자신은 선택 항목이 아니다. 전부면 checked,
                            // 일부면 indeterminate, 토글하면 선택 가능한 자식을 일괄 켜고/끈다.
                            <Checkbox
                              checked={childSelection.allSelected}
                              indeterminate={
                                childSelection.someSelected && !childSelection.allSelected
                              }
                              disabled={childSelection.selectableRows.length === 0}
                              onChange={(event) => {
                                source.setRowsSelected(
                                  childSelection.selectableRows,
                                  event.target.checked,
                                );
                              }}
                              aria-label="Select child rows"
                              size={SELECT_CHECKBOX_SIZE}
                            />
                          ) : (
                            <Checkbox
                              checked={selectedIds.has(row.getRowId())}
                              // 선택된 행은 항상 해제할 수 있다 — 판정은 전체선택 대상과 같은 헬퍼가 소유.
                              disabled={!canToggleRowSelection(row, selectedIds, source)}
                              onChange={() => source.toggleRowSelection(row)}
                              aria-label="Select row"
                              size={SELECT_CHECKBOX_SIZE}
                            />
                          )}
                        </div>
                      ) : null}
                      {showIndex ? (
                        <div className="indexCell" role={bodyCellRole}>
                          <Text
                            variant={defaultTextVariants["caption-bold"]}
                            className="cellContent"
                          >
                            {rowIndex + 1}
                          </Text>
                        </div>
                      ) : null}
                      {visibleColumns.map((column) => (
                        <div
                          key={column.key}
                          className={`cell ${resolveAlignClassName(column.align)} ${resolveCellPaddingClassName(column.cellPadding)} ${column.className ?? ""}`}
                          role={bodyCellRole}
                        >
                          {/* 잘림 마커 — 셀이 ellipsis로 잘리면 <TruncationTooltipHost>가 hover 시 전체 값을 띄운다.
                        마커만 달고(래퍼 없음) 측정·표시는 공유 host가 담당(RFC-truncation-tooltip §아키텍처).
                        단 render/format 컬럼(임의 ReactNode — 뱃지/아이콘/단위 등)은 제외한다: host가
                        textContent로 content를 만드는데, composite 셀은 자손 텍스트가 이어붙어 엉뚱한
                        값이 뜬다. plain 값 셀(String(value))만 마킹(textContent = 그 값). 커스텀
                        render/format의 툴팁은 후속 opt-in. */}
                          <Text
                            variant={defaultTextVariants["body-normal"]}
                            className="cellContent"
                            {...(column.render || column.format ? {} : { [TRUNCATE_ATTR]: "" })}
                          >
                            {renderCell(row, column)}
                          </Text>
                        </div>
                      ))}
                      {hasRowActions ? (
                        <div className="actionsCell" role={bodyCellRole}>
                          {row.renderRowActions()}
                        </div>
                      ) : null}
                      {isReorderable ? (
                        <div className="reorderCell" role={bodyCellRole}>
                          <button
                            type="button"
                            className="reorderHandle"
                            draggable
                            aria-label="Drag to reorder row"
                            onDragStart={(event) => handleDragStart(event, rowIndex)}
                            onDragEnd={handleDragEnd}
                          >
                            <DragHandleIcon />
                          </button>
                        </div>
                      ) : null}
                    </div>
                    {childRows.map((child, childIndex) => {
                      const isGroupEnd = childIndex === childRows.length - 1;
                      const isChildSelected =
                        selectionSurfaceEnabled && selectedIds.has(child.getRowId());
                      return (
                        // child row의 drag event는 top-level parent group의 drop boundary를 넓히기
                        // 위한 pointer delegation이다. child 자체는 활성화/키보드 대상이 아니므로
                        // focusable row로 바꾸지 않는다.
                        // eslint-disable-next-line jsx-a11y/interactive-supports-focus
                        <div
                          key={child.getRowId()}
                          className={resolveRowChildClassName({
                            selected: isChildSelected,
                            selectionDisabled: rowSelectionDisabled,
                          })}
                          style={rowStyle}
                          data-row-group-end={isGroupEnd ? "true" : "false"}
                          role="row"
                          aria-selected={selectionSurfaceEnabled ? isChildSelected : undefined}
                          onDragOver={(event) => handleDragOver(event, rowIndex)}
                          onDrop={(event) => handleDrop(event, rowIndex)}
                        >
                          {isDropTarget && dragDirection === "below" && isGroupEnd ? (
                            <div className="dropIndicator dropBelow" />
                          ) : null}
                          {showCheckbox ? (
                            // 부모 행 selectCell과 같은 이유로 전파만 끊는다.
                            // eslint-disable-next-line jsx-a11y/click-events-have-key-events
                            <div
                              className="selectCell"
                              role={bodyCellRole}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Checkbox
                                checked={selectedIds.has(child.getRowId())}
                                disabled={!canToggleRowSelection(child, selectedIds, source)}
                                onChange={() => source.toggleRowSelection(child)}
                                aria-label="Select row"
                                size={SELECT_CHECKBOX_SIZE}
                              />
                            </div>
                          ) : null}
                          {/* 번호 트랙의 빈 셀 — 하위 행은 번호를 갖지 않지만, 셀을 생략하면 표의
                          컬럼 커버리지가 행마다 달라지고 내용 셀 시작 라인도 흔들린다. */}
                          {showIndex ? <div className="indexCell" role={bodyCellRole} /> : null}
                          <div
                            className="rowChildCell"
                            role={bodyCellRole}
                            aria-colspan={childContentColumnSpan}
                          >
                            {renderRowChild?.(child, row)}
                          </div>
                        </div>
                      );
                    })}
                  </Fragment>
                );
              })
            )}
          </div>
          <DataTableAppendFooter
            source={source}
            snapshot={sourceSnapshot}
            sentinelRef={sentinelRef}
            messages={appendMessages}
            tableCellRole={bodyCellRole}
          />
        </DataTableScroller>
      </div>
    </Shell>
  );
}

// 보조 컨트롤: DataTableColumnMenuButton — 컬럼 가시성 토글 팝오버(테이블 밖 툴바에 배치)
export type DataTableColumnMenuPlacement = "top" | "bottom";

export type DataTableColumnMenuButtonProps<Row extends TableRow> = {
  columns: readonly DataTableColumn<Row>[];
  columnVisibility: DataTableColumnVisibility;
  onColumnVisibilityChange: (visibility: DataTableColumnVisibility) => void;
  menuPlacement?: DataTableColumnMenuPlacement;
  size?: IconButtonSize;
};

// 팝오버 프레임(포털·배치·바깥-클릭/포커스)은 c6 AnchoredPopup이 담당하고, 여기서는 그 Panel
// 안에 들어갈 컬럼 토글 목록 레이아웃만 정의한다.
const ColumnMenuList = styled.div`
  display: grid;
  min-width: 190px;
  padding: 8px 0;

  .columnMenuItem {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    color: ${({ theme }) => theme.color.text.default};
    font-size: ${({ theme }) => theme.typography.size.body};
    cursor: pointer;
  }

  /* AnchoredPopup Panel 배경이 surfaceRaised라, hover는 한 단계 위(surfaceRaisedHigh)로 대비를 준다. */
  .columnMenuItem:hover {
    background: ${({ theme }) => theme.color.bg.surfaceRaisedHigh};
  }

  .columnMenuCheckbox {
    pointer-events: none;
  }
`;

export function DataTableColumnMenuButton<Row extends TableRow>({
  columns,
  columnVisibility,
  onColumnVisibilityChange,
  menuPlacement = "bottom",
  size = "sm",
}: DataTableColumnMenuButtonProps<Row>) {
  // 팝오버 앵커/열림 상태는 AnchoredPopup의 hook이 소유한다(트리거 rect로 앵커링).
  const { anchor, open, openFromElement, close } = useAnchoredPopupAnchor();
  const hideableColumns = useMemo(() => columns.filter(isColumnHideable), [columns]);
  const isColumnVisible = useCallback(
    (column: DataTableColumn<Row>) => columnVisibility[column.key] ?? true,
    [columnVisibility],
  );

  const toggleColumnVisibility = useCallback(
    (key: string) => {
      const isVisible = columnVisibility[key] ?? true;
      onColumnVisibilityChange({
        ...columnVisibility,
        [key]: !isVisible,
      });
    },
    [columnVisibility, onColumnVisibilityChange],
  );

  if (hideableColumns.length === 0) {
    return null;
  }

  return (
    <>
      <IconButton
        text="Columns"
        size={size}
        variant="surface"
        selected={open}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={(event) => (open ? close() : openFromElement(event))}
      />
      <AnchoredPopup
        open={open}
        anchor={anchor}
        onClose={close}
        role="menu"
        aria-label="Toggle columns"
        placement={menuPlacement === "top" ? "top-end" : "bottom-end"}
      >
        <ColumnMenuList>
          {hideableColumns.map((column) => (
            <label key={column.key} className="columnMenuItem">
              <Checkbox
                className="columnMenuCheckbox"
                checked={isColumnVisible(column)}
                size="1rem"
                onChange={() => toggleColumnVisibility(column.key)}
              />
              <Text variant={defaultTextVariants["body-normal"]}>{column.header}</Text>
            </label>
          ))}
        </ColumnMenuList>
      </AnchoredPopup>
    </>
  );
}

// 헤더는 body의 세로 scrollport 밖에 두되 같은 가로 좌표계를 쓴다. body의 scrollLeft와
// scrollbar를 제외한 clientWidth를 header viewport에 그대로 복제해 fr 트랙까지 같은 폭으로 계산한다.
// 키보드 포커스가 header viewport를 스크롤하면 body에도 반영해 양쪽 입력의 컬럼 정렬을 유지한다.
function useDataTableHeaderScrollSync(scrollable: boolean) {
  const headerViewportRef = useRef<HTMLDivElement>(null);
  const bodyScrollerRef = useRef<HTMLDivElement>(null);
  const bodyGridRef = useRef<HTMLDivElement>(null);
  const pendingHeaderScrollRef = useRef<number | null>(null);
  const pendingBodyScrollRef = useRef<number | null>(null);

  const syncHeaderViewport = useCallback(() => {
    const headerViewport = headerViewportRef.current;
    const bodyScroller = bodyScrollerRef.current;
    if (!headerViewport || !bodyScroller) return;

    if (bodyScroller.clientWidth > 0) {
      headerViewport.style.inlineSize = `${bodyScroller.clientWidth}px`;
    }
    if (headerViewport.scrollLeft !== bodyScroller.scrollLeft) {
      headerViewport.scrollLeft = bodyScroller.scrollLeft;
      pendingHeaderScrollRef.current = headerViewport.scrollLeft;
    }
  }, []);

  // 컬럼/행/상태 변경으로 scrollbar 유무가 달라진 render도 paint 전에 다시 맞춘다.
  useLayoutEffect(() => {
    syncHeaderViewport();
  });

  useLayoutEffect(() => {
    const bodyScroller = bodyScrollerRef.current;
    const bodyGrid = bodyGridRef.current;
    if (!bodyScroller) return;

    const resizeObserver =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(syncHeaderViewport);
    resizeObserver?.observe(bodyScroller);
    if (bodyGrid) resizeObserver?.observe(bodyGrid);
    window.addEventListener("resize", syncHeaderViewport);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener("resize", syncHeaderViewport);
    };
  }, [scrollable, syncHeaderViewport]);

  const handleBodyScroll = useCallback((event: ReactUIEvent<HTMLDivElement>) => {
    const headerViewport = headerViewportRef.current;
    const mirrored = pendingBodyScrollRef.current;
    pendingBodyScrollRef.current = null;
    // A queued acknowledgement must not overwrite a newer native scroll in the other pane.
    if (mirrored === event.currentTarget.scrollLeft) return;
    if (headerViewport && headerViewport.scrollLeft !== event.currentTarget.scrollLeft) {
      headerViewport.scrollLeft = event.currentTarget.scrollLeft;
      pendingHeaderScrollRef.current = headerViewport.scrollLeft;
    }
  }, []);

  const handleHeaderScroll = useCallback((event: ReactUIEvent<HTMLDivElement>) => {
    const bodyScroller = bodyScrollerRef.current;
    const mirrored = pendingHeaderScrollRef.current;
    pendingHeaderScrollRef.current = null;
    if (mirrored === event.currentTarget.scrollLeft) return;
    if (bodyScroller && bodyScroller.scrollLeft !== event.currentTarget.scrollLeft) {
      bodyScroller.scrollLeft = event.currentTarget.scrollLeft;
      pendingBodyScrollRef.current = bodyScroller.scrollLeft;
    }
  }, []);

  // 헤더가 body scrollport의 형제여도 헤더 위 wheel/trackpad 입력은 표를 계속 스크롤한다.
  const handleHeaderWheel = useCallback(
    (event: WheelEvent) => {
      const bodyScroller = bodyScrollerRef.current;
      if (!bodyScroller) return;

      const deltaScale =
        event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? bodyScroller.clientHeight : 1;
      let inlineDelta = event.deltaX * deltaScale;
      let blockDelta = event.deltaY * deltaScale;
      if (event.shiftKey && inlineDelta === 0) {
        inlineDelta = blockDelta;
        blockDelta = 0;
      }
      if (getComputedStyle(bodyScroller).direction === "rtl") {
        inlineDelta *= -1;
      }

      const consumesInline =
        inlineDelta !== 0 && bodyScroller.scrollWidth > bodyScroller.clientWidth;
      const consumesBlock =
        blockDelta !== 0 && bodyScroller.scrollHeight > bodyScroller.clientHeight;
      if (!consumesInline && !consumesBlock) return;

      bodyScroller.scrollLeft += inlineDelta;
      bodyScroller.scrollTop += blockDelta;
      syncHeaderViewport();
      // c6 Scrollbar의 overscroll-behavior: contain과 같게, 해당 축의 overflow가 있으면 경계에서도
      // 바깥 page로 wheel을 넘기지 않는다. 헤더/본문 위 pointer 위치에 따른 차이를 만들지 않는다.
      event.preventDefault();
    },
    [syncHeaderViewport],
  );

  // React의 wheel 위임 listener는 브라우저에서 passive일 수 있어 preventDefault가 보장되지 않는다.
  // 이 seam만 native non-passive listener로 연결해 body 이동과 page scroll chaining을 정확히 나눈다.
  useLayoutEffect(() => {
    const headerViewport = headerViewportRef.current;
    if (!headerViewport) return;

    headerViewport.addEventListener("wheel", handleHeaderWheel, { passive: false });
    return () => headerViewport.removeEventListener("wheel", handleHeaderWheel);
  }, [handleHeaderWheel]);

  return {
    headerViewportRef,
    bodyScrollerRef,
    bodyGridRef,
    handleBodyScroll,
    handleHeaderScroll,
  };
}

// 내부 헬퍼: 스크롤 래퍼·컬럼 가시성 초기화·셀 렌더·정렬 사이클·드래그 고스트·className 빌더
function DataTableScroller({
  children,
  scrollable,
  scrollerRef,
  onScroll,
}: {
  children: ReactNode;
  scrollable: boolean;
  scrollerRef: Ref<HTMLDivElement>;
  onScroll: (event: ReactUIEvent<HTMLDivElement>) => void;
}) {
  if (scrollable) {
    // gutter="auto": 세로 스크롤바가 없으면 우측 gutter를 예약하지 않는다. header viewport는
    // 이 element의 clientWidth를 따라 실제 scrollbar가 차지한 폭만큼만 자동으로 줄어든다.
    return (
      <Scrollbar
        className="scroller"
        orientation="both"
        gutter="auto"
        ref={scrollerRef}
        onScroll={onScroll}
      >
        {children}
      </Scrollbar>
    );
  }

  return (
    <div className="scroller" ref={scrollerRef} onScroll={onScroll}>
      {children}
    </div>
  );
}

export function createDataTableColumnVisibility<Row extends TableRow>(
  columns: readonly DataTableColumn<Row>[],
): DataTableColumnVisibility {
  return Object.fromEntries(columns.map((column) => [column.key, column.visible ?? true]));
}

function isColumnHideable<Row extends TableRow>(column: DataTableColumn<Row>): boolean {
  return column.hideable ?? true;
}

function renderCell<Row extends TableRow>(row: Row, column: DataTableColumn<Row>) {
  if (column.render) {
    return column.render(row);
  }

  const value = row[column.key];
  if (column.format) {
    return column.format(value, row);
  }
  if (value == null) {
    return "";
  }
  return String(value);
}

function defaultSortDirection(sortable: DataTableSortable): DataTableSortDirection {
  return sortable === "desc" ? "desc" : "asc";
}

// initialSort는 key+방향을 정확히 표현하고 initialSortIndex보다 우선한다. 둘 다 마운트 시 한
// 번만 읽는 초기값이며, 컬럼 정책이 허용하지 않는 조합은 적용하지 않는다.
function resolveInitialSort<Row extends TableRow>(
  columns: readonly DataTableColumn<Row>[],
  initialSort: DataTableSortState | undefined,
  initialSortIndex: number | undefined,
): DataTableSortState | null {
  if (initialSort !== undefined) {
    const column = columns.find((candidate) => candidate.key === initialSort.columnKey);
    if (!column || !allowsSortDirection(column.sortable ?? "none", initialSort.direction)) {
      return null;
    }
    return { ...initialSort };
  }
  if (initialSortIndex === undefined) {
    return null;
  }
  const column = columns[initialSortIndex];
  if (column === undefined) {
    return null;
  }
  const sortable = column.sortable ?? "none";
  if (sortable === "none") {
    return null;
  }
  return { columnKey: column.key, direction: defaultSortDirection(sortable) };
}

function allowsSortDirection(
  sortable: DataTableSortable,
  direction: DataTableSortDirection,
): boolean {
  return sortable === "both" || sortable === direction;
}

// 헤더 클릭 3-state 사이클. 다른 컬럼이면 그 컬럼 기본 방향으로 시작하고,
// 같은 컬럼이면 허용 방향을 진행하다 끝에서 정렬을 해제(null)한다.
function cycleSort<Row extends TableRow>(
  current: DataTableSortState | null,
  column: DataTableColumn<Row>,
): DataTableSortState | null {
  const sortable = column.sortable ?? "none";
  if (sortable === "none") {
    return current;
  }
  const cycle: DataTableSortDirection[] =
    sortable === "both" ? ["asc", "desc"] : sortable === "desc" ? ["desc"] : ["asc"];
  const active = current?.columnKey === column.key ? current.direction : null;
  if (active === null) {
    return { columnKey: column.key, direction: cycle[0] };
  }
  const next = cycle[cycle.indexOf(active) + 1];
  return next ? { columnKey: column.key, direction: next } : null;
}

function ariaSortValue(
  direction: DataTableSortDirection | null,
): "ascending" | "descending" | "none" {
  if (direction === "asc") {
    return "ascending";
  }
  if (direction === "desc") {
    return "descending";
  }
  return "none";
}

// 재정렬 드래그 고스트: 마우스다운 시점의 행을 복제해 낮은 opacity로 띄운다. styled-components
// 자손 선택자와 grid CSS 변수가 적용되도록 Shell→grid→bodyGrid 계층을 흉내낸 래퍼에 넣고,
// 화면 밖(top:-10000px)에 두면 브라우저가 dragstart 직후 스냅샷으로 캡처해 커서를 따라다닌다.
function buildRowDragImage(rowEl: HTMLElement): HTMLElement | null {
  const shellEl = rowEl.closest<HTMLElement>("[data-scrollable]");
  const gridEl = rowEl.closest<HTMLElement>(".grid");
  const bodyGridEl = rowEl.parentElement;
  if (!shellEl || !gridEl || !bodyGridEl) {
    return null;
  }
  const width = rowEl.getBoundingClientRect().width;

  const ghost = document.createElement("div");
  ghost.className = shellEl.className;
  ghost.setAttribute("data-scrollable", "false");
  ghost.setAttribute("aria-hidden", "true");
  ghost.style.position = "fixed";
  ghost.style.top = "-10000px";
  ghost.style.left = "0";
  ghost.style.width = `${width}px`;
  ghost.style.margin = "0";
  ghost.style.opacity = "0.6";
  ghost.style.pointerEvents = "none";

  const gridGhost = document.createElement("div");
  gridGhost.className = gridEl.className;
  gridGhost.style.cssText = gridEl.style.cssText;

  const bodyGridGhost = document.createElement("div");
  bodyGridGhost.className = bodyGridEl.className;
  bodyGridGhost.style.cssText = bodyGridEl.style.cssText;

  const rowGhost = rowEl.cloneNode(true) as HTMLElement;
  rowGhost.classList.remove("dragOverRow");

  bodyGridGhost.appendChild(rowGhost);
  gridGhost.appendChild(bodyGridGhost);
  ghost.appendChild(gridGhost);
  document.body.appendChild(ghost);
  return ghost;
}

function resolveAlignClassName(align: DataTableAlign = "left"): string {
  switch (align) {
    case "center":
      return "alignCenter";
    case "right":
      return "alignRight";
    case "left":
      return "alignLeft";
  }
}

function resolveCellPaddingClassName(cellPadding: DataTableCellPadding = "default"): string {
  return cellPadding === "tight" ? "cellTight" : "";
}

// single과 multiple/replace는 기존 selection을 먼저 비우므로 현재 canSelectMore 상한이
// "선택 불가 행"의 근거가 아니다. 행/자식 고유 자격(canSelect) 또는 이미 담겨 해제 가능한
// 멤버가 하나라도 있으면 이 top-level selection unit을 교체 입력에 사용할 수 있다.
function canReplaceRowSelectionUnit(
  rows: readonly TableRowBase[],
  selectedIds: ReadonlySet<string>,
): boolean {
  return rows.some((row) => selectedIds.has(row.getRowId()) || row.canSelect());
}

function resolveRowClassName(options: {
  clickable: boolean;
  focusable: boolean;
  dragOver: boolean;
  selected?: boolean;
  currentMarker?: boolean;
  selectionDisabled?: boolean;
  extra?: string;
}): string {
  return [
    "row",
    "bodyRow",
    options.clickable ? "clickableRow" : "",
    options.focusable ? "focusableRow" : "",
    options.dragOver ? "dragOverRow" : "",
    options.selected ? "selectedRow" : "",
    options.currentMarker ? "currentMarkerRow" : "",
    options.selectionDisabled ? "selectionDisabledRow" : "",
    options.extra ?? "",
  ]
    .filter(Boolean)
    .join(" ");
}

// 하위 행은 bodyRow가 아니다 — 정렬·재정렬·행 활성화 대상이 아니고 hover 강조도 받지 않는다.
// 선택 강조만 최상위 행과 같은 규칙("그 행 체크박스가 checked면 강조")으로 공유한다.
function resolveRowChildClassName(options: {
  selected: boolean;
  selectionDisabled: boolean;
}): string {
  return [
    "row",
    "rowChildRow",
    options.selected ? "selectedRow" : "",
    options.selectionDisabled ? "selectionDisabledRow" : "",
  ]
    .filter(Boolean)
    .join(" ");
}
